import {DragDropProvider} from '@wandb/common/containers/DragDrop';
import {isNotNullOrUndefined} from '@wandb/common/util/types';
import {PanelExportContextProvider} from '@wandb/weave-ui/components/Panel2/PanelExportContext';
import React, {useCallback, useContext, useState} from 'react';
import {Editor, Node, NodeEntry, Range, Text, Transforms} from 'slate';
import {createEditor} from 'slate';
import {withHistory} from 'slate-history';
import {
  ReactEditor,
  RenderElementProps,
  RenderLeafProps,
  Slate,
} from 'slate-react';
import {withReact} from 'slate-react';

import {useSelector} from '../../state/hooks';
import {ReportViewRef} from '../../state/reports/types';
import {Ref as DiscussionThreadRef} from '../../state/views/discussionThread/types';
import * as ViewHooks from '../../state/views/hooks';
import {ReportWidthOption} from '../../state/views/report/types';
import {useAdminModeActive} from '../../util/admin';
import {useScrollToURLHash} from '../../util/document';
import {useIsGalleryReport} from '../../util/gallery';
import FloatingReportDiscussion from '../FloatingReportDiscussion';
import {ReportDiscussion} from '../ReportDiscussion';
import {
  ReportDiscussionContext,
  ReportDiscussionUpdaterContext,
} from '../ReportDiscussionContext';
import ReportPageSubscribePrompt from '../ReportPageSubscribePrompt';
import {useCompositionKeyboard} from './CompositionKeyboardContext';
import {FocusedContext} from './FocusedContext';
import HoveringToolbar from './HoveringToolbar';
import {autoScrollOnKeyDown} from './plugins/auto-scroll';
import {withBetterVoidHandling} from './plugins/better-void-handling';
import {withBlockQuotes} from './plugins/block-quotes';
import {
  BlockQuote,
  BlockQuoteElement,
  isBlockQuote,
} from './plugins/block-quotes';
import {
  CalloutBlock,
  CalloutBlockElement,
  CalloutLineElement,
  isCalloutBlock,
  isCalloutLine,
} from './plugins/callout-blocks';
import {withCallout} from './plugins/callout-blocks';
import {Captivate, CaptivateElement, isCaptivate} from './plugins/captivate';
import {withCaptivate} from './plugins/captivate';
import {
  CodeBlock,
  CodeBlockElement,
  CodeLineElement,
  isCodeBlock,
  isCodeLine,
  useCodeHighlighting,
} from './plugins/code-blocks';
import {withCode} from './plugins/code-blocks';
import {Craiyon, CraiyonElement, isCraiyon} from './plugins/craiyon';
import {withCraiyon} from './plugins/craiyon';
import {CTA, CTAElement, isCTA} from './plugins/cta-button';
import {withCTA} from './plugins/cta-button';
import {withDeleteLineBackwards} from './plugins/delete-line-backwards';
import {WBSlateDragDropContext} from './plugins/drag-drop';
import {withEmojis} from './plugins/emojis';
import {Gallery, GalleryElement, isGallery} from './plugins/gallery';
import {withGallerys} from './plugins/gallery';
import {hardBreakOnKeyDown} from './plugins/hard-break';
import {Heading, HeadingElement, isHeading} from './plugins/headings';
import {withHeadings} from './plugins/headings';
import {
  HorizontalRule,
  HorizontalRuleElement,
  isHorizontalRule,
} from './plugins/horizontal-rules';
import {withHorizontalRules} from './plugins/horizontal-rules';
import {Image, ImageElement, isImage} from './plugins/images';
import {withImages} from './plugins/images';
import {
  getMarkKeyFromNode,
  getThreadIDsFromNode,
  isInlineCommentMark,
  useInlineCommentHighlighting,
} from './plugins/inline-comment';
import {isLatex, Latex, LatexElement} from './plugins/latex';
import {withLatex} from './plugins/latex';
import {isLink, Link, LinkElement} from './plugins/links';
import {withLinks} from './plugins/links';
import {
  EditorWithLists,
  isList,
  isListItem,
  List,
  ListElement,
  ListItem,
  ListItemElement,
} from './plugins/lists';
import {withLists} from './plugins/lists';
import {markShortcutsOnKeyDown} from './plugins/mark-shortcuts';
import {MarkdownBlockElement} from './plugins/markdown-blocks';
import {withMarkdownBlocks} from './plugins/markdown-blocks';
import {isMarkdownBlock, MarkdownBlock} from './plugins/markdown-blocks-common';
import {withMarkdownShortcuts} from './plugins/markdown-shortcuts';
import {PanelGridElement} from './plugins/panel-grids';
import {withPanelGrids} from './plugins/panel-grids';
import {isPanelGrid, PanelGrid} from './plugins/PanelGridTypes';
import {isParagraph, Paragraph, ParagraphElement} from './plugins/paragraphs';
import {EditorWithPersistentBlankLines} from './plugins/persistent-blank-lines';
import {withPersistentBlankLine} from './plugins/persistent-blank-lines';
import {isReplit, Replit, ReplitElement} from './plugins/replit';
import {withReplit} from './plugins/replit';
import {
  isSoundCloud,
  SoundCloud,
  SoundCloudElement,
} from './plugins/soundcloud';
import {withSoundCloud} from './plugins/soundcloud';
import {isSpotify, Spotify, SpotifyElement} from './plugins/spotify';
import {withSpotify} from './plugins/spotify';
import {
  isTableOfContents,
  TableOfContents,
  TableOfContentsElement,
  withTableOfContents,
} from './plugins/table-of-contents';
import {
  isTable,
  isTableCell,
  isTableRow,
  TableCellElement,
  TableElement,
  TableRowElement,
} from './plugins/tables';
import {withTables} from './plugins/tables';
import {isTwitter, Twitter, TwitterElement} from './plugins/twitter';
import {withTwitter} from './plugins/twitter';
import {isVideo, Video, VideoElement} from './plugins/videos';
import {withVideos} from './plugins/videos';
import {
  isWeavePanel,
  isWeavePythonPanel,
  WeavePanel,
  WeavePanelElement,
  WeavePythonPanel,
  WeavePythonPanelElement,
  withWeavePanels,
  withWeavePythonPanels,
} from './plugins/weave-panels';
import {ShiftPressedContext} from './ShiftPressedContext';
import SlashMenu from './SlashMenu';
import {TypingContext} from './TypingContext';
import {stripZeroWidthChars} from './util';
import * as S from './WBSlate.styles';

type DOMNode = globalThis.Node;

const DOMNode = globalThis.Node;

const isDOMNode = (value: any): value is DOMNode => {
  return value instanceof DOMNode;
};

const hasEditableTarget = (
  editor: ReactEditor,
  target: EventTarget | null
): target is DOMNode => {
  return (
    isDOMNode(target) &&
    ReactEditor.hasDOMNode(editor, target, {editable: true})
  );
};

export function createWBSlateEditor(admin = false): Editor {
  return withCTA(
    withGallerys(
      withBlockQuotes(
        withHeadings(
          withReplit(admin)(
            withCaptivate(
              withSoundCloud(
                withSpotify(
                  withTwitter(
                    withCraiyon(
                      withVideos(
                        withEmojis(
                          withLatex(
                            withTableOfContents(
                              withHorizontalRules(
                                withCode(
                                  withCallout(
                                    withTables(
                                      withLists(
                                        withDeleteLineBackwards(
                                          withImages(
                                            withWeavePanels(
                                              withWeavePythonPanels(
                                                withPanelGrids(
                                                  withPersistentBlankLine(
                                                    withMarkdownBlocks(
                                                      withLinks(
                                                        withMarkdownShortcuts(
                                                          withBetterVoidHandling(
                                                            withReact(
                                                              withHistory(
                                                                createEditor()
                                                              )
                                                            )
                                                          )
                                                        )
                                                      )
                                                    )
                                                  )
                                                )
                                              )
                                            )
                                          )
                                        )
                                      )
                                    )
                                  )
                                )
                              )
                            )
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  );
}

export interface WBSlateProps {
  readOnly: boolean;
  widthMode: ReportWidthOption;
  value: WBSlateElement[];
  viewRef: ReportViewRef;
  viewID: string;
  onChange(value: Node[]): void;
}

const WBSlateElement: React.FC<RenderElementProps> = props => {
  const {element} = props;
  if (isParagraph(element)) {
    return <ParagraphElement {...props} element={element} />;
  }
  if (isListItem(element)) {
    return <ListItemElement {...props} element={element} />;
  }
  if (isList(element)) {
    return <ListElement {...props} element={element} />;
  }
  if (isBlockQuote(element)) {
    return <BlockQuoteElement {...props} element={element} />;
  }
  if (isGallery(element)) {
    return <GalleryElement {...props} element={element} />;
  }
  if (isHeading(element)) {
    return <HeadingElement {...props} element={element} />;
  }
  if (isCodeBlock(element)) {
    return <CodeBlockElement {...props} element={element} />;
  }
  if (isCodeLine(element)) {
    return <CodeLineElement {...props} element={element} />;
  }
  if (isCalloutBlock(element)) {
    return <CalloutBlockElement {...props} element={element} />;
  }
  if (isCalloutLine(element)) {
    return <CalloutLineElement {...props} element={element} />;
  }
  if (isLink(element)) {
    return <LinkElement {...props} element={element} />;
  }
  if (isMarkdownBlock(element)) {
    return <MarkdownBlockElement {...props} element={element} />;
  }
  if (isPanelGrid(element)) {
    return <PanelGridElement {...props} element={element} />;
  }
  if (isWeavePanel(element)) {
    return <WeavePanelElement {...props} element={element} />;
  }
  if (isWeavePythonPanel(element)) {
    return <WeavePythonPanelElement {...props} element={element} />;
  }
  if (isImage(element)) {
    return <ImageElement {...props} element={element} />;
  }
  if (isTable(element)) {
    return <TableElement {...props} element={element} />;
  }
  if (isTableRow(element)) {
    return <TableRowElement {...props} element={element} />;
  }
  if (isTableOfContents(element)) {
    return <TableOfContentsElement {...props} element={element} />;
  }
  if (isTableCell(element)) {
    return <TableCellElement {...props} element={element} />;
  }
  if (isHorizontalRule(element)) {
    return <HorizontalRuleElement {...props} element={element} />;
  }
  if (isLatex(element)) {
    return <LatexElement {...props} element={element} />;
  }
  if (isVideo(element)) {
    return <VideoElement {...props} element={element} />;
  }
  if (isCraiyon(element)) {
    return <CraiyonElement {...props} element={element} />;
  }
  if (isTwitter(element)) {
    return <TwitterElement {...props} element={element} />;
  }
  if (isSpotify(element)) {
    return <SpotifyElement {...props} element={element} />;
  }
  if (isReplit(element)) {
    return <ReplitElement {...props} element={element} />;
  }
  if (isSoundCloud(element)) {
    return <SoundCloudElement {...props} element={element} />;
  }
  if (isCaptivate(element)) {
    return <CaptivateElement {...props} element={element} />;
  }
  if (isCTA(element)) {
    return <CTAElement {...props} element={element} />;
  }
  // default to paragraph for unknowns, to prevent crashes after funky migrations
  return <ParagraphElement {...props} element={element as Paragraph} />;
};

const WBSlateLeaf: React.FC<RenderLeafProps> = props => {
  if (isTextWithComments(props.leaf)) {
    return <LeafNodeWithComments {...props}></LeafNodeWithComments>;
  }

  const typedLeaf = props.leaf as TextWithFormatting;
  return (
    <LeafNode
      data-test="leaf-node-with-formatting"
      {...props}
      leaf={typedLeaf}></LeafNode>
  );
};

const WBSlate: React.FC<WBSlateProps> = ({
  readOnly,
  widthMode,
  value,
  viewRef,
  viewID,
  onChange,
}) => {
  const admin = useAdminModeActive();
  const editor = React.useMemo(
    () => createWBSlateEditor(admin),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const initialFocused = ReactEditor.isFocused(editor);
  const [focused, setFocused] = useState(initialFocused);

  const [slashMenuOpen, setSlashMenuOpen] = React.useState(false);
  const {inlineCommentDetails} = useContext(ReportDiscussionContext);

  const view = useSelector(state => state.views.views[viewRef.id]);
  const parentViewId = view.parentId ?? view.id;

  const [dragItem, setDragItem] = React.useState<Node | undefined>(undefined);
  // This is true if any of the drag handle menus are open. Disables the popup on the other handles.
  const [dragHandleMenuOpen, setDragHandleMenuOpen] = React.useState(false);
  const dragContextValue = React.useMemo(
    () => ({
      parentViewId,
      dragItem,
      setDragItem,
      dragHandleMenuOpen,
      setDragHandleMenuOpen,
    }),
    [
      parentViewId,
      dragItem,
      setDragItem,
      dragHandleMenuOpen,
      setDragHandleMenuOpen,
    ]
  );

  const [typing, setTyping] = React.useState(true);
  const typingContextValue = React.useMemo(
    () => ({typing, setTyping}),
    [typing]
  );

  const [shiftPressed, setShiftPressed] = React.useState(false);
  const shiftPressedContextValue = React.useMemo(
    () => ({shiftPressed, setShiftPressed}),
    [shiftPressed]
  );

  const {startComposition, endComposition} = useCompositionKeyboard();

  // HAX: DO NOT REMOVE. THE "CONVERT ALL MARKDOWN" BUTTON IS RELIANT ON THIS.
  // "sry not sry" - Axel
  (window as any).editor = editor;
  (window as any).Editor = Editor;
  (window as any).ReactEditor = ReactEditor;
  (window as any).Transforms = Transforms;
  (window as any).value = value;

  useScrollToURLHash(3000);

  const {isGalleryReport} = useIsGalleryReport(viewID);

  const renderElement = React.useCallback(
    (props: RenderElementProps) => {
      // check if the focus has shifted from/in slate
      // if so, rerender WBSlate to rerender child elements
      const editorIsFocused = ReactEditor.isFocused(editor);
      setFocused(editorIsFocused);

      return <WBSlateElement {...props} />;
    },
    [editor]
  );
  const renderLeaf = React.useCallback(
    (props: RenderLeafProps) => {
      return <WBSlateLeaf {...props} />;
    },
    // this dep list allows re-rendering all the elements when inlineCommentDetails
    // get changed (i.e. user added inline text to comment) to call decorate
    // and properly highlight the temporary inline text that is getting commented on.
    // without this, slate doesn't re-render all the elements for performance optimization.
    // eslint-disable-next-line
    [inlineCommentDetails]
  );

  const addWeavePanel = React.useCallback(
    (panel: any) => {
      const weavePanelNode: Node = {
        type: 'weave-panel',
        children: [{text: ''}],
        config: {
          exp: panel.node,
          panelId: panel.panelId,
          panelConfig: panel.config,
        },
      };
      Editor.insertNode(editor, weavePanelNode);
    },
    [editor]
  );

  const codeDecorate = useCodeHighlighting(editor);
  const inlineDecorate = useInlineCommentHighlighting();

  const decorate = useCallback(
    ([node, path]: NodeEntry<Node>) => {
      // decorate the code syntax
      const codeRanges = codeDecorate([node, path]);
      // decorate the temporary inline text comments
      const inlineRanges = inlineDecorate([node, path]);
      return [...codeRanges, ...inlineRanges];
    },
    [codeDecorate, inlineDecorate]
  );

  const selectSlateRangeInViewMode = useCallback(() => {
    /* For slate to get selection from browser in view mode
          to enable inline text comment */
    if (!readOnly || isGalleryReport) {
      // edit mode already has onMouseUp implemented in Editable
      // inline text comment is disabled for gallery reports
      return;
    }

    const el = ReactEditor.toDOMNode(editor, editor);
    const root = el.getRootNode();

    if (root instanceof Document && root.getSelection != null) {
      const domSelection = root.getSelection();
      if (
        domSelection == null ||
        domSelection.type === 'None' ||
        domSelection.type === 'Caret'
      ) {
        // clear out the selection to not show hoveringToolbar and
        // not add old selected text in comment when clicking once
        Transforms.deselect(editor);
        return;
      }

      const range = ReactEditor.toSlateRange(editor, domSelection, {
        exactMatch: false,
        suppressThrow: false,
      });
      Transforms.select(editor, range);
    }
  }, [readOnly, isGalleryReport, editor]);

  // TODO(axel): figure out how to normalize on init in a cleaner fashion
  React.useLayoutEffect(() => {
    EditorWithPersistentBlankLines.ensureBlankLine(editor);
    if (!readOnly) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.start(editor, []));
    }
  }, [editor, readOnly]);

  return (
    <S.SlateWrapper data-test="wbslate-report-content">
      {isGalleryReport && (
        <FloatingReportDiscussion
          viewRef={viewRef}
          isFeatured={isGalleryReport}
        />
      )}
      <S.PrintReportHack />
      <Slate editor={editor} value={value} onChange={onChange}>
        <TypingContext.Provider value={typingContextValue}>
          {/* For letting child components know focus has changed */}
          <FocusedContext.Provider value={focused}>
            <ShiftPressedContext.Provider value={shiftPressedContextValue}>
              <SlashMenu
                open={slashMenuOpen}
                onClose={React.useCallback(() => setSlashMenuOpen(false), [])}
              />
              <HoveringToolbar readOnly={readOnly} />
              <PanelExportContextProvider addPanel={addWeavePanel}>
                {/* For reordering blocks */}
                <WBSlateDragDropContext.Provider value={dragContextValue}>
                  {/* For panels in panel grids */}
                  <DragDropProvider>
                    <S.StyledEditable
                      data-test="report-editable-content"
                      decorate={decorate}
                      readOnly={readOnly}
                      $widthMode={widthMode}
                      renderElement={renderElement}
                      renderLeaf={renderLeaf}
                      scrollSelectionIntoView={() => {}} // no-op to disable Slate's default scroll behavior when dragging an overflowed element
                      onMouseUp={() => {
                        try {
                          selectSlateRangeInViewMode();
                        } catch (e) {
                          // user selected the void elements,
                          // deselect the range to not accidentally open hovering toolbar
                          Transforms.deselect(editor);
                        }
                      }}
                      onCopyCapture={(
                        event: React.ClipboardEvent<HTMLDivElement>
                      ) => {
                        if (readOnly) {
                          stripZeroWidthChars(event);
                        }
                      }}
                      onPaste={(
                        event: React.ClipboardEvent<HTMLDivElement>
                      ) => {
                        // this is slightly modified from slate-react's internal onPaste handler
                        // we need this because slate doesn't handle paste correctly on Safari
                        if (
                          hasEditableTarget(editor, event.target) &&
                          !readOnly
                        ) {
                          event.preventDefault();
                          ReactEditor.insertData(editor, event.clipboardData);
                        }
                      }}
                      onCut={() => {
                        /** Ugly hack to fix cut on a single void node */
                        const {selection} = editor;
                        if (
                          selection &&
                          Range.isCollapsed(selection) &&
                          Editor.void(editor)
                        ) {
                          window.setTimeout(() => {
                            Editor.deleteBackward(editor);
                          });
                        }
                      }}
                      onKeyDown={e => {
                        if (autoScrollOnKeyDown(editor, e)) {
                          return;
                        }

                        if (markShortcutsOnKeyDown(editor, e)) {
                          return;
                        }

                        if (hardBreakOnKeyDown(editor, e)) {
                          return;
                        }

                        if (e.key === '/') {
                          window.setTimeout(() => {
                            setSlashMenuOpen(true);
                          });
                        }

                        // Prevents newline. Necessary in Firefox.
                        if (e.key === 'Enter') {
                          if (slashMenuOpen) {
                            e.preventDefault();
                          }
                        }

                        if (e.key === 'Tab') {
                          e.preventDefault();

                          if (EditorWithLists.onTab(editor, e)) {
                            return;
                          }

                          Transforms.insertText(editor, '\t');
                          return;
                        }

                        // Unfortunate hack because Slate doesn't
                        // detect backspaces on inline voids
                        // in Chrome.
                        if (e.key === 'Backspace') {
                          const inlineVoid = Editor.above(editor, {
                            match: n =>
                              Editor.isVoid(editor, n) &&
                              Editor.isInline(editor, n),
                          });
                          if (inlineVoid != null) {
                            e.preventDefault();
                            Editor.deleteBackward(editor);
                            return;
                          }
                        }

                        if (e.key === 'Shift') {
                          setShiftPressed(true);
                          editor.shiftPressed = true;
                        }

                        setTyping(true);
                      }}
                      onKeyUp={e => {
                        if (e.key === 'Shift') {
                          setShiftPressed(false);
                          editor.shiftPressed = false;
                        }
                      }}
                      onMouseMove={() => {
                        setTyping(false);
                      }}
                      onCompositionStart={startComposition}
                      onCompositionEnd={endComposition}
                    />
                  </DragDropProvider>
                  <ReportPageSubscribePrompt viewID={viewID} />
                  <ReportDiscussion isViewingReport={readOnly} />
                </WBSlateDragDropContext.Provider>
              </PanelExportContextProvider>
            </ShiftPressedContext.Provider>
          </FocusedContext.Provider>
        </TypingContext.Provider>
      </Slate>
    </S.SlateWrapper>
  );
};

export default WBSlate;

export const isTextWithComments = (node: Node): boolean =>
  Object.keys(node).some(isInlineCommentMark);

const LeafNodeWithComments: React.FC<
  RenderLeafProps & {leaf: TextWithFormatting}
> = props => {
  const {activeThreadRef, discussionThreadRefs} = useContext(
    ReportDiscussionContext
  );
  const {setActiveThread} = useContext(ReportDiscussionUpdaterContext);
  const threadIDs = getThreadIDsFromNode(props.leaf);
  const allThreadsParts = ViewHooks.useParts(discussionThreadRefs);
  const allThreads = allThreadsParts.map((part, i) => ({
    ...part,
    ref: discussionThreadRefs[i],
  }));

  const openInlineComments = useCallback(() => {
    const relatedThreadRefs = allThreads
      .filter(
        thread =>
          threadIDs.includes(thread.id) && isNotNullOrUndefined(thread.ref)
      )
      .map(thread => thread.ref);

    if (relatedThreadRefs == null || relatedThreadRefs.length <= 0) {
      return;
    }

    setActiveThread(
      relatedThreadRefs[0],
      true,
      relatedThreadRefs as DiscussionThreadRef[]
    );
  }, [allThreads, threadIDs, setActiveThread]);

  const activeThread = ViewHooks.usePartMaybe(activeThreadRef);

  if (activeThread != null && threadIDs.includes(activeThread.id)) {
    const childLeafWithActiveComment = {
      ...props.leaf,
      activeInlineComment: true,
      onClick: (e: any) => {
        openInlineComments();
      },
    };
    return (
      <LeafNode
        data-test="leaf-node-with-comment"
        {...props}
        leaf={childLeafWithActiveComment as TextWithFormatting}
      />
    );
  }

  const childLeafWithComment = {
    ...props.leaf,
    inlineComment: true,
    onClick: (e: any) => {
      openInlineComments();
    },
  };
  return (
    <LeafNode {...props} leaf={childLeafWithComment as TextWithFormatting} />
  );
};

export interface TextWithFormatting extends Text {
  strong?: boolean;
  emphasis?: boolean;
  inlineCode?: boolean;
  underline?: boolean;
  delete?: boolean;

  codeComment?: boolean;
  inlineComment?: boolean;
  activeInlineComment?: boolean;
  tempInlineComment?: boolean;
  operator?: boolean;
  url?: boolean;
  keyword?: boolean;
  variable?: boolean;
  regex?: boolean;
  number?: boolean;
  boolean?: boolean;
  tag?: boolean;
  constant?: boolean;
  symbol?: boolean;
  attr?: boolean;
  selector?: boolean;
  punctuation?: boolean;
  string?: boolean;
  char?: boolean;
  function?: boolean;
  class?: boolean;
  onClick?: React.MouseEventHandler<HTMLSpanElement>;
}

export const isTextWithFormatting = (node: Node): node is TextWithFormatting =>
  typeof (node as any).text === 'string';

const LeafNode: React.FC<RenderLeafProps & {leaf: TextWithFormatting}> = ({
  attributes,
  children,
  leaf,
}) => {
  // this id is for scrolling to the text node from inline text comments
  const id = getMarkKeyFromNode(leaf);
  return (
    <S.Leaf
      id={id}
      strong={leaf.strong}
      emphasis={leaf.emphasis}
      inlineCode={leaf.inlineCode}
      underline={leaf.underline}
      delete={leaf.delete}
      codeComment={leaf.codeComment}
      inlineComment={leaf.inlineComment}
      activeInlineComment={leaf.activeInlineComment}
      tempInlineComment={leaf.tempInlineComment}
      operator={leaf.operator}
      url={leaf.url}
      keyword={leaf.keyword}
      variable={leaf.variable}
      regex={leaf.regex}
      number={leaf.number}
      boolean={leaf.boolean}
      tag={leaf.tag}
      constant={leaf.constant}
      symbol={leaf.symbol}
      attr={leaf.attr}
      selector={leaf.selector}
      punctuation={leaf.punctuation}
      string={leaf.string}
      char={leaf.char}
      function={leaf.function}
      isClass={leaf.class}
      onClick={leaf.onClick}
      {...attributes}>
      {children}
    </S.Leaf>
  );
};

// When you add a type or edit one, make sure our report pre-rendering
// logic in host/src/report/slate.ts is also updated

export type WBSlateElement =
  | Paragraph
  | List
  | ListItem
  | CodeBlock
  | CalloutBlock
  | BlockQuote
  | Gallery
  | Heading
  | Link
  | MarkdownBlock
  | WeavePanel
  | WeavePythonPanel
  | PanelGrid
  | Image
  | HorizontalRule
  | TableOfContents
  | Latex
  | Video
  | Craiyon
  | Twitter
  | Spotify
  | Replit
  | SoundCloud
  | Captivate
  | CTA;
